import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@emotion/styled';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfitrollLogo from 'assets/images/profitroll-logo-colored.svg?react';
import JobAltoLogo from 'assets/icons/jobatlo-logo.svg?react';
import { isLocalizedDomain } from 'helpers/getLocale';
import BinaryChoice from './components/BinaryChoice';
import EmojiLikertScale from './components/EmojiLikertScale';
import MultiChoice from './components/MultiChoice';
import TrueFalse from './components/TrueFalse';
import ProgressArc from './components/ProgressArc';
import WonderlicTimer from './components/WonderlicTimer';
import Welcome from './components/Welcome';
import VoucherIntro from './components/VoucherIntro';
import PhaseIntro from './components/PhaseIntro';
import Consent from './components/Consent';
import InfoWizard from './components/InfoWizard';
import SignUpGate from './components/SignUpGate';
import Purchase from './components/Purchase';
import LanguageDropdown from './components/LanguageDropdown';
import { PHASE_INFO } from './constants';
import wonderlic_w17 from 'assets/images/wonderlic/w_17.webp';
import wonderlic_w29 from 'assets/images/wonderlic/w_29.webp';
import wonderlic_w32 from 'assets/images/wonderlic/w_32.webp';
import wonderlic_w40 from 'assets/images/wonderlic/w_40.webp';
import wonderlic_w45 from 'assets/images/wonderlic/w_45.webp';
import wonderlic_w49 from 'assets/images/wonderlic/w_49.webp';
import {
  assessmentLoad,
  assessmentSubmitAnswer,
  assessmentPatchSession,
  assessmentLinkUser,
  assessmentValidateCode,
  assessmentValidateToken,
  assessmentWonderlicStart,
} from 'actions/assessment';
import {
  forgetRememberedVoucherCredential,
  getRememberedVoucherCredential,
  recoverRememberedVoucher,
} from './sessionStorage';
import { isAssessmentSessionError } from './sessionRecovery';

import { getLocalizedPath } from 'helpers/getLocale';
import SEO from 'components/SEO';

// The assessment runs in a chrome-free layout, so the logo above the card
// is brand-conditional here too. isLocalizedDomain() now honours config.brand,
// so the dev server picks the right asset.
const Logo = isLocalizedDomain() ? JobAltoLogo : ProfitrollLogo;

// Site Header + Footer are only shown on the assessment intro screen
// (/assessment). All later steps stay on the focused, chrome-free layout.
// Aliased away from the local `Header` styled component below.
const SiteHeader = lazy(() => import('components/Header'));
const SiteFooter = lazy(() => import('components/Footer'));

const WONDERLIC_IMAGES = {
  w_17: wonderlic_w17,
  w_29: wonderlic_w29,
  w_32: wonderlic_w32,
  w_40: wonderlic_w40,
  w_45: wonderlic_w45,
  w_49: wonderlic_w49,
};

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: 20,
  backgroundColor: '#f5f5f5',
});

const MetaContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100vh',
  padding: '24px 20px 40px',
  backgroundColor: '#f5f5f5',
});

const Card = styled('div')({
  position: 'relative',
  backgroundColor: '#fff',
  borderRadius: 24,
  padding: 32,
  maxWidth: 600,
  width: '100%',
});

const TimerSlot = styled('div')({
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 1,
});

const WideCard = styled('div')({
  backgroundColor: '#fff',
  borderRadius: 24,
  padding: 40,
  maxWidth: 760,
  width: '100%',
});

const NarrowCard = styled('div')({
  backgroundColor: '#fff',
  borderRadius: 24,
  padding: 32,
  maxWidth: 520,
  width: '100%',
});

const OuterLogoWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'maxw',
})(({ maxw = 760 }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  maxWidth: maxw,
  marginBottom: 24,
}));

// Jobalto's SVG was authored fill: white (for the dark site chrome). The
// assessment runs on a light gray background, so override the path fills to a
// dark colour. Profitroll's coloured logo already paints itself dark, so the
// rule is a no-op there.
const OuterLogoImg = styled(Logo)({
  height: 36,
  ...(isLocalizedDomain() ? { '& path': { fill: '#1a1a1a' } } : {}),
});

const OuterBack = styled('button')({
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  padding: '6px 4px',
  cursor: 'pointer',
  fontFamily: 'Rubik-Regular, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 500,
  color: '#000',
  '&:hover': { textDecoration: 'underline' },
});

const Header = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: 32,
});

const PhaseLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey[400],
  marginTop: 8,
  marginBottom: 16,
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

const Content = styled('div')({
  marginBottom: 32,
});

// Pinned to the bottom of the viewport during the question flow. Desktop only —
// on phones the in-card ProgressArc already gives a clear signal and the bar
// would crowd the on-screen keyboard. Sits above the language dropdown's z so
// it stays visible while the user scrolls.
const BottomProgressBar = styled('div')(({ theme }) => ({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  height: 16,
  backgroundColor: theme.palette.grey[200],
  zIndex: 1100,
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const BottomProgressFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'pct',
})(({ pct }) => ({
  height: '100%',
  width: `${Math.max(0, Math.min(100, pct))}%`,
  backgroundColor: '#79c912',
  transition: 'width 320ms ease-out',
}));

const SkipRow = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  marginTop: -16,
  marginBottom: 8,
});

const SkipButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontFamily: 'Rubik-Regular, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.grey[600],
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.grey[800],
    textDecoration: 'underline',
  },
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
  padding: 40,
});

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: 'center',
}));

const STORAGE_KEY = 'assessment_state';
const ADVANCE_DELAY_MS = 250;

// All path builders go through getLocalizedPath so navigate() calls on
// jobalto.com produce `/<locale>-<country>/assessment/...` URLs (matching the
// routes table), not bare `/assessment/...` which 404s into the welcome
// fallback. On profitroll.com.ua, getLocalizedPath is a no-op.
const sectionPath = (phaseIndex) =>
  getLocalizedPath(`/assessment/section/${phaseIndex + 1}`);
const questionPath = (phaseIndex, questionIndex) =>
  getLocalizedPath(`/assessment/section/${phaseIndex + 1}/q/${questionIndex + 1}`);
const reviewSkippedPath = (phaseIndex) =>
  getLocalizedPath(`/assessment/section/${phaseIndex + 1}/review-skipped`);

// Walk phases in order and return the deep-link to the first question whose id
// has no recorded answer. Used on resume so the user lands on the next question
// to answer — not the last one they already answered (the stored current_path).
// Returns null when every question is answered.
export function firstUnansweredQuestionPath(loadedPhases, answersMap) {
  for (let pi = 0; pi < loadedPhases.length; pi += 1) {
    const qs = loadedPhases[pi].questions || [];
    for (let qi = 0; qi < qs.length; qi += 1) {
      const id = qs[qi]?.id;
      if (id && !(id in answersMap)) return questionPath(pi, qi);
    }
  }
  return null;
}

// Where a server-provided session (cross-device resume) should land. Jumps to
// the first UNANSWERED question, not session.current_path — that stored path is
// the last question the user *viewed* (already answered), so resuming there
// replays it. Falls back to results when every question is answered, or to the
// stored path when no answers exist yet. Returns null when there's nothing to
// resume to. Same-device reloads do NOT use this — they restore the local
// sessionStorage lastPath instead.
export function serverResumeTarget(session, loadedPhases, currentPathname) {
  const answersMap = session?.answers || {};
  if (Object.keys(answersMap).length > 0 && loadedPhases.length) {
    return firstUnansweredQuestionPath(loadedPhases, answersMap)
      || getLocalizedPath('/assessment/results');
  }
  if (session?.current_path && session.current_path !== currentPathname) {
    return session.current_path;
  }
  return null;
}

// Strip the optional `/<locale>-<country>` prefix (used on jobalto.com) so the
// rest of parseLocation can pattern-match against the route-relative path.
// On profitroll.com.ua URLs have no locale prefix, so this is a no-op there.
// Locale codes are 2 chars (en, ua, de, …). Country is usually 2 chars but
// jobalto uses the synthetic `int` (3 chars) for the i18n catch-all, so allow
// 2-3 chars on the country side.
const LOCALE_PREFIX_RE = /^\/[a-z]{2}-[a-z]{2,3}(?=\/|$)/i;
const stripLocalePrefix = (pathname) => pathname.replace(LOCALE_PREFIX_RE, '') || '/';

export const isVoucherRedeemPath = (pathname) =>
  stripLocalePrefix(pathname).startsWith('/assessment/redeem/');

// Mirror of InfoWizard.STEPS — keep in sync. Drives the share of the bottom
// progress bar attributed to the pre-test demographic wizard.
const INFO_WIZARD_STEPS = ['intro', 'name', 'email', 'age', 'gender', 'certainty', 'occupations'];

function parseLocation(rawPathname) {
  const pathname = stripLocalePrefix(rawPathname);
  if (pathname === '/assessment' || pathname === '/assessment/') return { view: 'welcome' };
  if (pathname.startsWith('/assessment/code')) return { view: 'welcome' };
  // Voucher redeem deep-link (/assessment/redeem/:token from the email).
  if (pathname.startsWith('/assessment/redeem')) return { view: 'welcome' };
  if (pathname.startsWith('/assessment/voucher-intro')) return { view: 'voucherIntro' };
  if (pathname.startsWith('/assessment/purchase')) return { view: 'purchase' };
  if (pathname.startsWith('/assessment/sign-up-gate')) return { view: 'signup' };
  if (pathname.startsWith('/assessment/consent')) return { view: 'consent' };
  if (pathname.startsWith('/assessment/info')) {
    // Extract the wizard sub-step from the URL (intro/name/email/age/...).
    // Used by the overall progress bar so the info wizard contributes to it.
    const seg = pathname.replace(/^\/assessment\/info\/?/, '').split('/')[0] || 'intro';
    return { view: 'info', infoStep: seg };
  }
  const qMatch = matchPath('/assessment/section/:phase/q/:question', pathname);
  if (qMatch) {
    const phase = Math.max(1, parseInt(qMatch.params.phase, 10) || 1) - 1;
    const question = Math.max(1, parseInt(qMatch.params.question, 10) || 1) - 1;
    return { view: 'question', phaseIndex: phase, questionIndex: question };
  }
  const rMatch = matchPath('/assessment/section/:phase/review-skipped', pathname);
  if (rMatch) {
    const phase = Math.max(1, parseInt(rMatch.params.phase, 10) || 1) - 1;
    return { view: 'reviewSkippedIntro', phaseIndex: phase };
  }
  const sMatch = matchPath('/assessment/section/:phase', pathname);
  if (sMatch) {
    const phase = Math.max(1, parseInt(sMatch.params.phase, 10) || 1) - 1;
    return { view: 'phaseIntro', phaseIndex: phase };
  }
  return { view: 'welcome' };
}

export default function AssessmentPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const route = useMemo(() => parseLocation(location.pathname), [location.pathname]);

  const [answers, setAnswers] = useState({});
  const [localAnswer, setLocalAnswer] = useState(null);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Set when submitting the current answer fails (offline / network). We keep
  // the user on the question with a retry affordance instead of advancing and
  // silently dropping the answer (the server never received it).
  const [submitError, setSubmitError] = useState(false);
  const [linkAttempted, setLinkAttempted] = useState(false);
  // Wonderlic phase timer: { phase_started_at, time_limit_seconds, server_now }.
  // Server-stamped, refresh-safe; populated when the user enters the Wonderlic
  // phase and reused for the duration via the idempotent /wonderlic/start.
  const [wonderlicTimer, setWonderlicTimer] = useState(null);
  const wonderlicTimedOutRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);

  // Wonderlic skip-and-revisit feature: track question IDs the user clicked
  // "Skip" on during pass 1, plus a flag indicating we're in the review pass.
  // Both are persisted to sessionStorage so refreshes mid-Wonderlic don't lose
  // state. Cleared when the user leaves the Wonderlic phase.
  const [wonderlicSkipped, setWonderlicSkipped] = useState([]);
  const [wonderlicReviewing, setWonderlicReviewing] = useState(false);

  const advanceTimerRef = useRef(null);
  // performance.now() snapshot stamped when each question first renders;
  // read in advanceToNext to produce the client tta_ms sent to the server.
  const questionShownAtRef = useRef(null);
  const assessmentData = useSelector((state) => state.assessment);
  const user = useSelector((state) => state.app?.user);

  const phases = useMemo(() => assessmentData?.phases || [], [assessmentData?.phases]);
  const phaseIndex = route.phaseIndex ?? 0;
  const questionIndex = route.questionIndex ?? 0;
  const currentPhase = phases[phaseIndex];
  const currentQuestion = currentPhase?.questions?.[questionIndex];
  const isDemo = sessionStorage.getItem('isDemo') === '1';

  const restoreState = useCallback(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    try { return JSON.parse(saved); } catch { return null; }
  }, []);

  const persistLocal = useCallback((extra = {}) => {
    const saved = restoreState() || {};
    const next = { ...saved, ...extra };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, [restoreState]);

  // Wipe every scrap of assessment state from the browser. Used when the local
  // session is rejected by the server (stale token, deleted session, etc.) so
  // the user can start fresh instead of being trapped on an error screen.
  const clearLocalSession = useCallback(() => {
    ['sessionId', 'sessionToken', STORAGE_KEY, 'isDemo', 'voucherId', 'assessment_user_info', 'assessment_resume_email_sent_to']
      .forEach((k) => sessionStorage.removeItem(k));
    setAnswers({});
  }, []);

  const startOver = useCallback(() => {
    clearLocalSession();
    forgetRememberedVoucherCredential();
    setError(null);
    navigate(getLocalizedPath('/assessment'), { replace: true });
  }, [clearLocalSession, navigate]);

  const navigateToVoucherCode = useCallback((reviewMode) => {
    const saved = getRememberedVoucherCredential();
    navigate(getLocalizedPath('/assessment/code'), {
      replace: true,
      state: reviewMode ? {
        redeemReviewMode: true,
        redeemReviewCode: saved?.kind === 'code' ? saved.value : '',
        redeemReviewToken: saved?.kind === 'token' ? saved.value : '',
        ...(reviewMode === 'email_mismatch' ? { redeemErrorKey: 'codeErrorEmailMismatch' } : {}),
      } : { sessionRecovery: true },
    });
  }, [navigate]);

  const recoverVoucherSession = useCallback(async () => {
    if (recoveryAttemptedRef.current) return null;
    const saved = getRememberedVoucherCredential();
    if (!saved) return null;
    recoveryAttemptedRef.current = true;
    try {
      return await recoverRememberedVoucher((kind, value) => dispatch(kind === 'token'
        ? assessmentValidateToken(value)
        : assessmentValidateCode(value)));
    } catch (err) {
      const status = err?.status || err?.response?.status || err?.error?.code;
      if ([404, 409].includes(Number(status))) forgetRememberedVoucherCredential();
      return null;
    }
  }, [dispatch]);

  const loadAssessment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    let recoveryReviewMode = null;
    try {
      let result;
      try {
        result = await dispatch(assessmentLoad());
      } catch (err) {
        if (!isAssessmentSessionError(err)) throw err;
        const recovered = await recoverVoucherSession();
        if (recovered?.reviewMode === 'awaiting_email' || recovered?.reviewMode === 'email_mismatch') {
          recoveryReviewMode = recovered.reviewMode;
          throw err;
        }
        if (!recovered?.sessionId) throw err;
        if (recovered.session?.answers) setAnswers(recovered.session.answers);
        // One retry only, with credentials re-minted for the SAME voucher.
        result = await dispatch(assessmentLoad());
      }
      if (result?.sessionId) sessionStorage.setItem('sessionId', result.sessionId);
      if (result?.sessionToken) sessionStorage.setItem('sessionToken', result.sessionToken);
      if (result?.phases) {
        // eslint-disable-next-line no-console
        console.log('[assessment] phases breakdown:', result.phases.map((p) => ({ name: p.name, n: p.questions?.length })));
      }
      return result;
    } catch (err) {
      console.error('Failed to load assessment:', err);
      if (isAssessmentSessionError(err)) {
        navigateToVoucherCode(recoveryReviewMode);
      } else {
        setError(t('Assessment.loadingError'));
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigateToVoucherCode, recoverVoucherSession, t]);

  // Bootstrap: resume from saved state or, if logged in, from server.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // The token in a redeem URL is authoritative. Do not let an older local
      // or authenticated assessment resume and navigate away while Welcome is
      // still redeeming this voucher in parallel.
      if (isVoucherRedeemPath(location.pathname)) return;

      const saved = restoreState();
      let hasLocalSession = !!sessionStorage.getItem('sessionId') && !!sessionStorage.getItem('sessionToken');

      if (!hasLocalSession && route.view !== 'purchase' && !location.pathname.endsWith('/code')) {
        const recovered = await recoverVoucherSession();
        if (cancelled) return;
        if (recovered?.reviewMode === 'awaiting_email' || recovered?.reviewMode === 'email_mismatch') {
          navigateToVoucherCode(recovered.reviewMode);
          return;
        }
        if (recovered?.sessionId) {
          await handleUnlocked(recovered, true);
          return;
        }
        hasLocalSession = !!sessionStorage.getItem('sessionId') && !!sessionStorage.getItem('sessionToken');
      }

      if (!hasLocalSession && !['welcome', 'purchase'].includes(route.view)) {
        // No remembered voucher (or recovery failed). Preserve local answers
        // and let the user re-enter the original code or link.
        navigateToVoucherCode();
        return;
      }

      if (saved?.isComplete) {
        navigate(getLocalizedPath('/assessment/results'), { replace: true });
        return;
      }

      // Authenticated resume from server (any device).
      if (user && !isDemo && !linkAttempted) {
        setLinkAttempted(true);
        try {
          if (hasLocalSession) {
            // Attach this session to the signed-in user. A different server
            // session id cannot replace the local id without a matching token.
            const linked = await dispatch(assessmentLinkUser());
            const session = linked?.session;
            if (session && !cancelled) {
              if (session.session_id !== sessionStorage.getItem('sessionId')) return;
              if (session.answers) setAnswers(session.answers);
              // Server-driven resume only when the user landed on welcome with no
              // path of their own. On any deep link reload, trust the URL.
              if (route.view === 'welcome') {
                let loadedPhases = phases;
                if (!loadedPhases.length && Object.keys(session.answers || {}).length > 0) {
                  try { loadedPhases = (await loadAssessment())?.phases || []; } catch { loadedPhases = []; }
                }
                if (!cancelled) {
                  const target = serverResumeTarget(session, loadedPhases, location.pathname);
                  if (target) navigate(target, { replace: true });
                }
              }
            }
          }
        } catch (e) {
          console.error('Resume failed:', e);
        }
      }

      // Direct deep-link to an unlocked assessment screen needs a valid local
      // session. A signed-in account alone is not enough to open voucher intro.
      if (route.view === 'phaseIntro' || route.view === 'question' || route.view === 'voucherIntro' || route.view === 'consent' || route.view === 'info') {
        if (!phases.length && hasLocalSession) {
          try { await loadAssessment(); }
          catch {
            // loadAssessment routes invalid credentials to code entry and
            // leaves saved progress intact; network errors retain a retry.
            return;
          }
        }
        if (saved?.answers) setAnswers(saved.answers);
        if (Array.isArray(saved?.wonderlicSkipped)) setWonderlicSkipped(saved.wonderlicSkipped);
        if (typeof saved?.wonderlicReviewing === 'boolean') setWonderlicReviewing(saved.wonderlicReviewing);
        return;
      }

      // Returning user on welcome with saved progress — only redirect if the
      // load succeeds. Never wipe local answers on a transient failure.
      if (saved && hasLocalSession && route.view === 'welcome' && !location.pathname.endsWith('/code')) {
        try {
          if (!phases.length) await loadAssessment();
          if (cancelled) return;
          setAnswers(saved.answers || {});
          if (Array.isArray(saved.wonderlicSkipped)) setWonderlicSkipped(saved.wonderlicSkipped);
          if (typeof saved.wonderlicReviewing === 'boolean') setWonderlicReviewing(saved.wonderlicReviewing);
          const resumePath = saved.lastPath || sectionPath(saved.currentPhaseIndex || 0);
          navigate(resumePath, { replace: true });
        } catch {
          // loadAssessment handles the recovery/error UI.
        }
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Persist progress locally on every meaningful change.
  useEffect(() => {
    if (route.view === 'welcome' || route.view === 'signup') return;
    const state = {
      view: route.view,
      currentPhaseIndex: phaseIndex,
      currentQuestionIndex: questionIndex,
      answers,
      lastPath: location.pathname,
      isComplete: false,
      wonderlicSkipped,
      wonderlicReviewing,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [route.view, phaseIndex, questionIndex, answers, location.pathname, wonderlicSkipped, wonderlicReviewing]);

  // Mirror the URL to the server (POST, not PATCH — PATCH preflight is blocked
  // by upstream CORS) so re-entering the voucher code later resumes here.
  useEffect(() => {
    if (route.view === 'welcome' || route.view === 'signup') return;
    const hasSession = !!sessionStorage.getItem('sessionId') && !!sessionStorage.getItem('sessionToken');
    if (!hasSession) return;
    const path = location.pathname;
    const id = setTimeout(() => {
      dispatch(assessmentPatchSession({ current_path: path })).catch(() => {});
    }, 800);
    return () => clearTimeout(id);
  }, [location.pathname, route.view, dispatch]);

  // Reset transient per-question state when the current question changes.
  // Critical: also clear any pending auto-advance timer queued by the previous
  // question. Without this, a 250ms timer scheduled on question N can fire
  // after the user has navigated to N+1 (Skip, Back, deep-link), where the
  // closure reads the *current* question/index and silently moves the user
  // forward again — looking like "auto-advance with no answer selected".
  useEffect(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    setLocalAnswer(currentQuestion ? answers[currentQuestion.id] ?? null : null);
    setIsAdvancing(false);
    // Restamp "question shown" on every id change — covers forward, Back,
    // Skip, and the Wonderlic review pass. Cleared (set to null) when there
    // is no current question so a stale value can't leak across screens.
    questionShownAtRef.current = currentQuestion ? performance.now() : null;
  }, [currentQuestion?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  // Stamp Wonderlic phase start time on the server the first time we render
  // anything inside the Wonderlic phase. Idempotent server-side, so refreshes
  // and back-navs return the same start timestamp.
  useEffect(() => {
    if (currentPhase?.type !== 'wonderlic') return;
    if (wonderlicTimer) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await dispatch(assessmentWonderlicStart());
        if (cancelled) return;
        // Backend wraps responses in `{data: ...}` for some routes; accept both.
        const payload = resp?.data || resp;
        if (payload?.phase_started_at) {
          setWonderlicTimer({
            phase_started_at: payload.phase_started_at,
            time_limit_seconds:
              payload.time_limit_seconds ?? currentPhase?.timeLimitSeconds,
            server_now: payload.server_now,
          });
        }
      } catch (e) {
        // If timer setup fails, fall back to client-only countdown using the
        // phase's declared time limit. The validity score will flag the lack
        // of a server stamp, which is the correct behavior.
        if (!cancelled && currentPhase?.timeLimitSeconds) {
          setWonderlicTimer({
            phase_started_at: new Date().toISOString(),
            time_limit_seconds: currentPhase.timeLimitSeconds,
            server_now: new Date().toISOString(),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentPhase?.type, wonderlicTimer, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // When leaving the Wonderlic phase, clear the timer state so re-entry
  // (e.g., during a fresh attempt after reset) re-fetches it. Also clears
  // the skip-list and review flag since they're Wonderlic-specific.
  useEffect(() => {
    if (currentPhase?.type !== 'wonderlic' && wonderlicTimer) {
      setWonderlicTimer(null);
      wonderlicTimedOutRef.current = false;
      setWonderlicSkipped([]);
      setWonderlicReviewing(false);
    }
  }, [currentPhase?.type, wonderlicTimer]);

  const handleWonderlicTimeout = useCallback(() => {
    if (wonderlicTimedOutRef.current) return;
    wonderlicTimedOutRef.current = true;
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    // Auto-advance to the next phase. Unanswered Wonderlic items will simply
    // be missing; any answer submitted from this point would be marked
    // `late=true` server-side anyway.
    if (currentPhase?.type === 'wonderlic' && phaseIndex < phases.length - 1) {
      navigate(sectionPath(phaseIndex + 1));
    }
  }, [currentPhase?.type, phaseIndex, phases.length, navigate]);

  // Section-local progress: "A/CC" where CC is the question count of *this* section.
  const inSectionNumber = questionIndex + 1;
  const inSectionTotal = currentPhase?.questions?.length || 0;

  // Overall progress across all phases AND the pre-test info wizard — drives
  // the fixed bottom bar shown across the whole flow. Counts the user's
  // *position* (steps navigated past), not answered count, so skipped
  // Wonderlic items still advance it. The info wizard contributes
  // INFO_WIZARD_STEPS slots so the bar visibly fills while the user is
  // still filling out demographics.
  const overallProgress = useMemo(() => {
    if (!phases.length) return { current: 0, total: 0, pct: 0 };
    const questionsTotal = phases.reduce((sum, p) => sum + (p.questions?.length || 0), 0);
    const total = questionsTotal + INFO_WIZARD_STEPS.length;
    if (!total) return { current: 0, total: 0, pct: 0 };
    if (route.view === 'welcome' || route.view === 'voucherIntro' || route.view === 'consent' || route.view === 'signup') {
      return { current: 0, total, pct: 0 };
    }
    if (route.view === 'info') {
      const idx = INFO_WIZARD_STEPS.indexOf(route.infoStep || 'intro');
      const completed = Math.max(0, idx);
      return { current: completed, total, pct: (completed / total) * 100 };
    }
    // Question flow (or any post-info screen) — info wizard is fully behind us.
    let completed = INFO_WIZARD_STEPS.length;
    for (let i = 0; i < phaseIndex && i < phases.length; i += 1) {
      completed += phases[i].questions?.length || 0;
    }
    completed += Math.max(0, Math.min(questionIndex, inSectionTotal));
    return { current: completed, total, pct: (completed / total) * 100 };
  }, [phases, phaseIndex, questionIndex, inSectionTotal, route.view, route.infoStep]);

  // Navigation between meta-screens.
  const handleUnlocked = async (result, fromRecovery = false) => {
    if (!fromRecovery) recoveryAttemptedRef.current = false;
    // Code accepted. Sign-in is optional — the voucher code itself identifies the user
    // for cross-device resume. If a user is already signed in, link the session as a bonus.
    sessionStorage.removeItem('isDemo');
    if (user) {
      try { await dispatch(assessmentLinkUser()); } catch {}
    }

    const session = result?.session;

    // Completed assessments go straight to results.
    if (session?.is_complete || session?.completed_at) {
      navigate(getLocalizedPath('/assessment/results'), { replace: true });
      return;
    }

    // Mid-assessment voucher: rehydrate answers, then resume on the first
    // UNANSWERED question. The stored current_path is the last question the
    // user *viewed* (already answered), so navigating there would replay it.
    const answersMap = session?.answers || {};
    if (session?.answers) setAnswers(session.answers);
    const resumePath = session?.current_path;
    const hasResumeInfo = Object.keys(answersMap).length > 0
      || (resumePath
        && resumePath.startsWith('/assessment/')
        && resumePath !== '/assessment'
        && resumePath !== '/assessment/code');

    if (hasResumeInfo) {
      // Need phases loaded to map answer ids → the next question.
      let loadedPhases = phases;
      if (!loadedPhases.length) {
        try {
          const loaded = await loadAssessment();
          loadedPhases = loaded?.phases || [];
        } catch {
          navigate(getLocalizedPath('/assessment/consent'));
          return;
        }
      }

      if (Object.keys(answersMap).length > 0 && loadedPhases.length) {
        const nextPath = firstUnansweredQuestionPath(loadedPhases, answersMap);
        // Every question answered but session not finalized → results.
        navigate(nextPath || getLocalizedPath('/assessment/results'), {
          replace: true, state: nextPath ? undefined : { collectProcessFeedback: true },
        });
        return;
      }

      // No answers yet but a valid stored path (e.g. left off on consent/info).
      if (resumePath) {
        navigate(resumePath, { replace: true });
        return;
      }
    }

    // Fresh voucher (or older-shape response with no session field): show the
    // instructions without purchase/code controls, then continue to consent.
    navigate(getLocalizedPath('/assessment/voucher-intro'), { replace: true });
  };
  const handleDemoStart = () => {
    // Demo: anonymous, straight to consent.
    navigate(getLocalizedPath('/assessment/consent'));
  };
  const handleVoucherIntroStart = () => navigate(getLocalizedPath('/assessment/consent'));
  const handleConsent = () => navigate(getLocalizedPath('/assessment/info'));
  const handleInfoComplete = async () => {
    if (!phases.length) {
      try { await loadAssessment(); } catch { return; }
    }
    navigate(sectionPath(0));
  };
  const handleBeginPhase = () => navigate(questionPath(phaseIndex, 0));

  // Advance out of the Wonderlic phase entirely. Used by the review-pass when
  // there's nothing left to review, and by the "skip review" action.
  const advanceOutOfWonderlic = () => {
    setWonderlicReviewing(false);
    const isLastPhase = phaseIndex >= phases.length - 1;
    if (isLastPhase) {
      persistLocal({ isComplete: true });
      navigate(getLocalizedPath('/assessment/results'), { state: { collectProcessFeedback: true } });
    } else {
      navigate(sectionPath(phaseIndex + 1));
    }
  };

  const advanceToNext = async (answerForCurrentQuestion) => {
    if (!currentQuestion || !answerForCurrentQuestion) return;
    // Guard against a stale timer firing after navigation: the queued answer
    // was scheduled against a different question than the one now on screen.
    if (answerForCurrentQuestion.questionId !== currentQuestion.id) return;
    const newAnswers = { ...answers, [currentQuestion.id]: answerForCurrentQuestion };
    setAnswers(newAnswers);
    // Client-measured time-on-question. Null → action omits the field and
    // the server falls back to its own delta-from-prev-submission logic.
    const ttaMs = questionShownAtRef.current != null
      ? Math.round(performance.now() - questionShownAtRef.current)
      : null;
    try {
      try {
        await dispatch(assessmentSubmitAnswer(currentQuestion.id, answerForCurrentQuestion, ttaMs));
      } catch (err) {
        if (!isAssessmentSessionError(err)) throw err;
        const recovered = await recoverVoucherSession();
        if (!recovered?.sessionId) {
          navigateToVoucherCode(recovered?.reviewMode);
          setIsAdvancing(false);
          return;
        }
        await dispatch(assessmentSubmitAnswer(currentQuestion.id, answerForCurrentQuestion, ttaMs));
      }
      setSubmitError(false);
    } catch (err) {
      // The answer never reached the server (offline / network). Do NOT
      // advance — surface a retry so the answer isn't silently lost.
      console.error('Submit failed:', err);
      setSubmitError(true);
      setIsAdvancing(false);
      return;
    }

    const qid = currentQuestion.id;

    // Review-pass: the user is answering a previously-skipped Wonderlic
    // question. Drop it from the skip list, then jump to the next still-
    // skipped question or fall out of the phase if none remain.
    if (wonderlicReviewing && currentPhase?.type === 'wonderlic') {
      const remaining = wonderlicSkipped.filter((id) => id !== qid);
      setWonderlicSkipped(remaining);
      if (remaining.length === 0) {
        advanceOutOfWonderlic();
        return;
      }
      const nextIdx = currentPhase.questions.findIndex((q) => q.id === remaining[0]);
      if (nextIdx >= 0) {
        navigate(questionPath(phaseIndex, nextIdx));
      } else {
        advanceOutOfWonderlic();
      }
      return;
    }

    const isLastQuestionInPhase = questionIndex >= (currentPhase?.questions?.length || 0) - 1;
    const isLastPhase = phaseIndex >= phases.length - 1;

    // First-pass end of Wonderlic with skipped items and time still on the
    // clock: divert to the review intro instead of advancing to the next phase.
    if (
      isLastQuestionInPhase
      && currentPhase?.type === 'wonderlic'
      && wonderlicSkipped.length > 0
      && !wonderlicTimedOutRef.current
    ) {
      navigate(reviewSkippedPath(phaseIndex));
      return;
    }

    if (isLastQuestionInPhase && isLastPhase) {
      persistLocal({ isComplete: true });
      navigate(getLocalizedPath('/assessment/results'), { state: { collectProcessFeedback: true } });
    } else if (isLastQuestionInPhase) {
      navigate(sectionPath(phaseIndex + 1));
    } else {
      navigate(questionPath(phaseIndex, questionIndex + 1));
    }
  };

  const handleSkip = () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (!currentQuestion || currentPhase?.type !== 'wonderlic') return;
    const qid = currentQuestion.id;

    // Review-pass: skipping again moves to the next still-skipped item without
    // changing the list. If we're already on the last one, fall out of the
    // phase — the user has chosen not to commit an answer.
    if (wonderlicReviewing) {
      const idx = wonderlicSkipped.indexOf(qid);
      const nextQid = idx >= 0 && idx < wonderlicSkipped.length - 1
        ? wonderlicSkipped[idx + 1]
        : null;
      if (nextQid) {
        const nextIdx = currentPhase.questions.findIndex((q) => q.id === nextQid);
        if (nextIdx >= 0) {
          navigate(questionPath(phaseIndex, nextIdx));
          return;
        }
      }
      advanceOutOfWonderlic();
      return;
    }

    // First pass: append to the skipped list (idempotent), then either advance
    // to the next sequential question or — if this is the last question of
    // the phase — show the review-skipped intro.
    const newSkipped = wonderlicSkipped.includes(qid)
      ? wonderlicSkipped
      : [...wonderlicSkipped, qid];
    setWonderlicSkipped(newSkipped);

    const isLastQuestionInPhase = questionIndex >= (currentPhase?.questions?.length || 0) - 1;
    if (isLastQuestionInPhase) {
      navigate(reviewSkippedPath(phaseIndex));
    } else {
      navigate(questionPath(phaseIndex, questionIndex + 1));
    }
  };

  const handleStartReview = () => {
    if (!currentPhase || wonderlicSkipped.length === 0) {
      advanceOutOfWonderlic();
      return;
    }
    setWonderlicReviewing(true);
    const firstIdx = currentPhase.questions.findIndex((q) => q.id === wonderlicSkipped[0]);
    if (firstIdx >= 0) {
      navigate(questionPath(phaseIndex, firstIdx));
    } else {
      advanceOutOfWonderlic();
    }
  };

  const handleSkipReview = () => {
    advanceOutOfWonderlic();
  };

  const handleSelect = (answerObj) => {
    if (isAdvancing) return;
    setSubmitError(false);
    setLocalAnswer(answerObj);
    setIsAdvancing(true);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => advanceToNext(answerObj), ADVANCE_DELAY_MS);
  };

  const handleBack = () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    setSubmitError(false);
    setIsAdvancing(false);
    if (questionIndex > 0) {
      navigate(questionPath(phaseIndex, questionIndex - 1));
    } else {
      // First question of any section → return to that section's intro screen.
      navigate(sectionPath(phaseIndex));
    }
  };

  // Back from a section intro screen — go to the previous section's last
  // question, or to the info wizard if this is the first section.
  const handlePhaseIntroBack = () => {
    if (phaseIndex > 0) {
      const prevLen = phases[phaseIndex - 1]?.questions?.length || 0;
      navigate(questionPath(phaseIndex - 1, Math.max(0, prevLen - 1)));
    } else {
      navigate(getLocalizedPath('/assessment/info'));
    }
  };

  // ---------- meta-screen render ----------
  // Logical back for the meta/intro screens. A raw navigate(-1) here escaped
  // the flow (or jumped to the very start), so route each meta view to the
  // screen immediately before it. The info wizard has no internal back button,
  // so step back through its sub-steps and exit to consent from the first one.
  // (Question / phase-intro / review screens use handleBack / handlePhaseIntroBack.)
  const goOuterBack = () => {
    if (route.view === 'consent' || route.view === 'signup') {
      navigate(getLocalizedPath('/assessment'));
      return;
    }
    if (route.view === 'info') {
      const idx = INFO_WIZARD_STEPS.indexOf(route.infoStep || 'intro');
      if (idx > 0) {
        navigate(getLocalizedPath(`/assessment/info/${INFO_WIZARD_STEPS[idx - 1]}`));
      } else {
        navigate(getLocalizedPath('/assessment/consent'));
      }
      return;
    }
    // Entry screens (welcome / code) and purchase: history back is correct.
    navigate(-1);
  };
  const renderHeader = (maxw = 760) => (
    <OuterLogoWrap maxw={maxw}>
      <OuterBack type="button" onClick={goOuterBack}>{t('Assessment.codeBack')}</OuterBack>
      <OuterLogoImg />
    </OuterLogoWrap>
  );

  // The bottom progress bar is shown across every assessment screen, not just
  // the question flow. Computed once here and rendered as a sibling of
  // whatever screen renderContent() returns. Hidden on mobile (display:none
  // breakpoint inside BottomProgressBar).
  const progressBar = (
    <BottomProgressBar
      role="progressbar"
      aria-label={t('Assessment.overallProgressLabel', { defaultValue: 'Assessment progress' })}
      aria-valuemin={0}
      aria-valuemax={overallProgress.total || 0}
      aria-valuenow={overallProgress.current}
    >
      <BottomProgressFill pct={overallProgress.pct} />
    </BottomProgressBar>
  );

  const renderContent = () => {

  if (isLoading) {
    return (
      <Container>
        <Card>
          <LoadingContainer>
            <CircularProgress size={48} />
            <Typography>{t('Assessment.loadingTitle')}</Typography>
          </LoadingContainer>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <LoadingContainer>
            <ErrorText>{error}</ErrorText>
            <Button variant="contained" onClick={loadAssessment} sx={{ mt: 2, borderRadius: 25 }}>
              {t('Assessment.loadingRetry')}
            </Button>
            <Button variant="text" onClick={startOver} sx={{ mt: 1, textTransform: 'none' }}>
              {t('Assessment.loadingStartOver')}
            </Button>
          </LoadingContainer>
        </Card>
      </Container>
    );
  }

  if (route.view === 'welcome') {
    // Show site header + footer only on the very first /assessment screen.
    // /assessment/code (and every subsequent step) stays on the focused layout.
    const isIntroStep = !location.pathname.endsWith('/code');
    if (isIntroStep) {
      return (
        <Suspense fallback={null}>
          <SiteHeader />
          <MetaContainer>
            <WideCard><Welcome onUnlocked={handleUnlocked} onDemoStart={handleDemoStart} /></WideCard>
            <LanguageDropdown />
          </MetaContainer>
          <SiteFooter />
        </Suspense>
      );
    }
    return (
      <MetaContainer>
        {renderHeader()}
        <WideCard><Welcome onUnlocked={handleUnlocked} onDemoStart={handleDemoStart} /></WideCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }
  if (route.view === 'signup') {
    if (user) {
      // Already authenticated — skip the gate.
      navigate(getLocalizedPath('/assessment/consent'), { replace: true });
      return null;
    }
    return (
      <MetaContainer>
        {renderHeader()}
        <WideCard><SignUpGate redirectTo="/assessment/consent" /></WideCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }
  if (route.view === 'voucherIntro') {
    return (
      <MetaContainer>
        <OuterLogoWrap>
          <OuterLogoImg />
        </OuterLogoWrap>
        <WideCard><VoucherIntro onStart={handleVoucherIntroStart} /></WideCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }
  if (route.view === 'consent') {
    return (
      <MetaContainer>
        {renderHeader()}
        <WideCard><Consent onAccept={handleConsent} /></WideCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }
  if (route.view === 'info') {
    return (
      <MetaContainer>
        {renderHeader(520)}
        <NarrowCard><InfoWizard onComplete={handleInfoComplete} /></NarrowCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }
  if (route.view === 'purchase') {
    return (
      <MetaContainer>
        {renderHeader()}
        <WideCard><Purchase /></WideCard>
        <LanguageDropdown />
      </MetaContainer>
    );
  }

  // ---------- assessment screens ----------
  if (!phases.length) {
    return (
      <Container>
        <Card>
          <LoadingContainer>
            <CircularProgress size={48} />
            <Typography>{t('Assessment.loadingTitle')}</Typography>
          </LoadingContainer>
        </Card>
      </Container>
    );
  }

  if (route.view === 'reviewSkippedIntro') {
    if (!currentPhase) { navigate(getLocalizedPath('/assessment'), { replace: true }); return null; }
    // If for some reason we landed here with nothing to review, just move on.
    if (wonderlicSkipped.length === 0) {
      advanceOutOfWonderlic();
      return null;
    }
    const lastQ = currentPhase.questions?.length ? currentPhase.questions.length - 1 : 0;
    return (
      <MetaContainer>
        <OuterLogoWrap maxw={600}>
          <OuterBack
            type="button"
            onClick={() => navigate(questionPath(phaseIndex, lastQ))}
          >
            {t('Assessment.backLink')}
          </OuterBack>
          <OuterLogoImg />
        </OuterLogoWrap>
        <Card>
          {wonderlicTimer && (
            <TimerSlot>
              <WonderlicTimer
                phaseStartedAt={wonderlicTimer.phase_started_at}
                timeLimitSeconds={wonderlicTimer.time_limit_seconds}
                serverNow={wonderlicTimer.server_now}
                onTimeout={handleWonderlicTimeout}
              />
            </TimerSlot>
          )}
          <Content>
            <Typography
              variant="h5"
              sx={{ textAlign: 'center', mb: 2, fontWeight: 700 }}
            >
              {t('Assessment.wonderlicReviewHeading')}
            </Typography>
            <Typography
              sx={{ textAlign: 'center', mb: 3, color: 'text.secondary', lineHeight: 1.5 }}
            >
              {t('Assessment.wonderlicReviewBody', {
                count: wonderlicSkipped.length,
                defaultValue: 'You still have time. Want to revisit the {{count}} question(s) you skipped?',
              })}
            </Typography>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={handleSkipReview}
                sx={{ borderRadius: 25, textTransform: 'none', px: 3 }}
              >
                {t('Assessment.wonderlicReviewSkip')}
              </Button>
              <Button
                variant="contained"
                onClick={handleStartReview}
                sx={{ borderRadius: 25, textTransform: 'none', px: 3 }}
              >
                {t('Assessment.wonderlicReviewBegin')}
              </Button>
            </div>
          </Content>
        </Card>
        <LanguageDropdown />
      </MetaContainer>
    );
  }

  if (route.view === 'phaseIntro') {
    if (!currentPhase) { navigate(getLocalizedPath('/assessment'), { replace: true }); return null; }
    // The calibration phase mixes item types (yes/no, Likert, multi-choice)
    // so we use the phase-level type for the intro copy, not the first
    // question's type. Other phases stay first-item-driven for back-compat.
    const introType = currentPhase.type === 'calibration'
      ? 'calibration'
      : currentPhase.questions?.[0]?.type;
    return (
      <MetaContainer>
        <OuterLogoWrap maxw={600}>
          <OuterBack type="button" onClick={handlePhaseIntroBack}>
            {t('Assessment.backLink')}
          </OuterBack>
          <OuterLogoImg />
        </OuterLogoWrap>
        <Card>
          <PhaseIntro
            phaseIndex={phaseIndex}
            totalPhases={phases.length}
            questionType={introType}
            questionCount={currentPhase.questions?.length || 0}
            minutes={
              currentPhase?.timeLimitSeconds
                ? Math.round(currentPhase.timeLimitSeconds / 60)
                : undefined
            }
            onBegin={handleBeginPhase}
          />
        </Card>
        <LanguageDropdown />
      </MetaContainer>
    );
  }

  if (!currentQuestion) {
    return (
      <Container>
        <Card>
          <LoadingContainer><Typography>{t('Assessment.noQuestions')}</Typography></LoadingContainer>
        </Card>
      </Container>
    );
  }

  const renderQuestion = () => {
    switch (currentQuestion.type) {
      case 'holland':
        return (
          <BinaryChoice
            leftOption={currentQuestion.left}
            rightOption={currentQuestion.right}
            selected={localAnswer?.selected || null}
            onSelect={(choice) => handleSelect({
              questionId: currentQuestion.id,
              selected: choice,
              selectedType: choice === 'left' ? currentQuestion.leftType : currentQuestion.rightType,
            })}
            title={t('Assessment.binaryProfessionTitle')}
          />
        );
      case 'bigfive':
        return (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontSize: 24,
                lineHeight: 1.3,
                textAlign: 'center',
                maxWidth: 460,
                mx: 'auto',
              }}
            >
              {currentQuestion.text}
            </Typography>
            <EmojiLikertScale
              value={localAnswer?.value || null}
              onChange={(value) => handleSelect({
                questionId: currentQuestion.id, value,
                factor: currentQuestion.factor, reverse: currentQuestion.reverse,
              })}
            />
          </>
        );
      case 'wonderlic':
        return (
          <MultiChoice
            question={currentQuestion.text}
            options={currentQuestion.options.map((opt, idx) => ({ id: idx, text: opt }))}
            selectedId={localAnswer?.selectedIndex ?? null}
            onSelect={(idx) => handleSelect({ questionId: currentQuestion.id, selectedIndex: idx })}
            imageSrc={WONDERLIC_IMAGES[currentQuestion.id]}
            imageAlt={currentQuestion.id}
          />
        );
      case 'gardner':
      case 'rean':
        return (
          <TrueFalse
            question={currentQuestion.text}
            selected={localAnswer?.value ?? null}
            onSelect={(value) => handleSelect({ questionId: currentQuestion.id, value })}
          />
        );
      default:
        return <Typography>{t('Assessment.unknownQuestionType')}</Typography>;
    }
  };

  const isWonderlicPhase = currentPhase?.type === 'wonderlic';

  return (
    <MetaContainer>
      <OuterLogoWrap maxw={600}>
        <OuterBack type="button" onClick={handleBack} disabled={isAdvancing}>
          {t('Assessment.backLink')}
        </OuterBack>
        <OuterLogoImg />
      </OuterLogoWrap>
      <Card>
        {isWonderlicPhase && wonderlicTimer && (
          <TimerSlot>
            <WonderlicTimer
              phaseStartedAt={wonderlicTimer.phase_started_at}
              timeLimitSeconds={wonderlicTimer.time_limit_seconds}
              serverNow={wonderlicTimer.server_now}
              onTimeout={handleWonderlicTimeout}
            />
          </TimerSlot>
        )}
        <Header>
          <ProgressArc current={inSectionNumber} total={inSectionTotal} />
          {currentPhase && (
            <PhaseLabel variant="caption">
              {PHASE_INFO[currentQuestion?.type]?.titleKey
                ? t(`Assessment.${PHASE_INFO[currentQuestion?.type].titleKey}`)
                : currentPhase.name}
            </PhaseLabel>
          )}
        </Header>
        <Content>{renderQuestion()}</Content>
        {submitError && (
          <div role="alert" style={{ marginTop: 8, marginBottom: 8, textAlign: 'center' }}>
            <Typography sx={{ color: 'error.main', mb: 1, fontSize: 14, lineHeight: 1.4 }}>
              {t('Assessment.submitError', {
                defaultValue: "Couldn't save your answer. Check your connection and try again.",
              })}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => { if (localAnswer) advanceToNext(localAnswer); }}
              sx={{ borderRadius: 25, textTransform: 'none', px: 3 }}
            >
              {t('Assessment.submitRetry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        )}
        {isWonderlicPhase && (
          <SkipRow>
            <SkipButton
              type="button"
              variant="text"
              onClick={handleSkip}
              disabled={isAdvancing}
            >
              {wonderlicReviewing
                ? t('Assessment.wonderlicSkipAgain', { defaultValue: 'Skip again' })
                : t('Assessment.wonderlicSkip', { defaultValue: 'Skip — answer later' })}
            </SkipButton>
          </SkipRow>
        )}
      </Card>
    </MetaContainer>
  );
  };

  return (
    <>
      <SEO page="assessment" includeHreflang />
      {renderContent()}
      {progressBar}
    </>
  );
}
